import React, { useState, useRef, useEffect } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { getApiUrl } from '@/config/api';
import { safeJsonParse } from '@/utils/apiUtils';
import { MessageSquare, Bot, XCircle, Lightbulb } from '@/utils/icons';

/**
 * Message interface for chat history
 */
interface ChatMessage {
  id: string;
  type: 'user' | 'ai';
  content: string;
  timestamp: Date;
  loading?: boolean;
  sources?: Array<{
    document_title: string;
    similarity: number;
  }>;
}

/**
 * Props for the CoachChat component
 */
interface CoachChatProps {
  selectedTeam: {
    id: number;
    name: string;
    organization: {
      name: string;
    };
  } | null;
  days: number;
}

/**
 * AI-powered chat component for coaches to ask questions about athlete data.
 * Integrates with OpenAI to provide intelligent insights about team performance.
 */
const CoachChat: React.FC<CoachChatProps> = ({ selectedTeam, days }) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { isDark } = useTheme();

  /**
   * Scroll to bottom of messages when new message is added
   */
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  /**
   * Initialize chat with welcome message when team is selected
   */
  useEffect(() => {
    if (selectedTeam && messages.length === 0) {
      const welcomeMessage: ChatMessage = {
        id: 'welcome',
        type: 'ai',
        content: `**Welcome to your AI Coaching Assistant**

I analyze your team's performance data and provide evidence-based insights from your knowledge base.

**I can help you with:**

**Data Analysis** - Review performance metrics and patterns from the past ${days} days  
**Knowledge Base Search** - Find relevant information from your uploaded documents  
**Trend Identification** - Spot patterns and anomalies in athlete data  
**Actionable Recommendations** - Suggest specific interventions based on evidence  

**Example questions:**

- "Which athletes need attention this week?"
- "What patterns do you see in recovery data?"
- "What does the knowledge base say about HRV optimization?"
- "How can I improve team sleep quality?"
- "What recommendations do you have based on current trends?"

**What would you like to know about ${selectedTeam.name}?**`,
        timestamp: new Date()
      };
      setMessages([welcomeMessage]);
    }
  }, [selectedTeam, days]);

  /**
   * Send message to AI chat API
   */
  const sendMessage = async () => {
    if (!inputMessage.trim() || !selectedTeam || isLoading) return;

    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      type: 'user',
      content: inputMessage.trim(),
      timestamp: new Date()
    };

    const loadingMessage: ChatMessage = {
      id: Date.now().toString() + '_loading',
      type: 'ai',
      content: 'Analyzing your team data...',
      timestamp: new Date(),
      loading: true
    };

    setMessages(prev => [...prev, userMessage, loadingMessage]);
    setInputMessage('');
    setIsLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('access');
      const response = await fetch(getApiUrl('/api/ai/chat/'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          message: userMessage.content,
          team_id: selectedTeam.id,
          days: days,
          session_id: currentSessionId
        })
      });

      const result = await safeJsonParse<{ 
        response: string; 
        session_id?: string;
        retrieved_chunks?: Array<{ document_title: string; similarity: number }>;
        error?: string;
      }>(response, 'sending chat message');
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to get AI response');
      }

      // Store session ID for conversation continuity
      if (result.data!.session_id && !currentSessionId) {
        setCurrentSessionId(result.data!.session_id);
      }

      const aiMessage: ChatMessage = {
        id: Date.now().toString() + '_ai',
        type: 'ai',
        content: result.data!.response,
        timestamp: new Date(),
        sources: result.data!.retrieved_chunks
      };

      // Replace loading message with actual response
      setMessages(prev => prev.slice(0, -1).concat([aiMessage]));

    } catch (err) {
      console.error('Chat error:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to get response';
      setError(errorMessage);
      
      const aiErrorMessage: ChatMessage = {
        id: Date.now().toString() + '_error',
        type: 'ai',
        content: `Sorry, I encountered an error: ${errorMessage}\n\nPlease try again with a different question.`,
        timestamp: new Date()
      };

      // Replace loading message with error message
      setMessages(prev => prev.slice(0, -1).concat([aiErrorMessage]));
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Handle Enter key press in input
   */
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  /**
   * Clear chat history
   */
  const clearChat = () => {
    setMessages([]);
    setError(null);
    
    // Re-add welcome message if team is selected
    if (selectedTeam) {
      const welcomeMessage: ChatMessage = {
        id: 'welcome_new',
        type: 'ai',
        content: `Chat cleared! How can I help you analyze ${selectedTeam.name}'s performance data?`,
        timestamp: new Date()
      };
      setMessages([welcomeMessage]);
    }
  };

  /**
   * Get suggested questions based on enhanced coaching capabilities
   */
  const getSuggestedQuestions = () => [
    "Which team members need immediate attention based on their data?",
    "What patterns do you see in the performance metrics?",
    "What insights can you provide from the knowledge base?",
    "How can I improve team performance and outcomes?",
    "What does the data tell us about current trends?",
    "What recommendations do you have based on the analysis?",
    "Are there any concerning patterns in the data?",
    "What best practices should we consider implementing?"
  ];

  if (!selectedTeam) {
    return (
      <div className={`cgm-card rounded-2xl p-8 shadow-xl text-center`}>
        <div className="flex items-center justify-center mb-4">
          <MessageSquare className="w-12 h-12 cgm-icon-data" />
        </div>
        <h3 className="text-xl font-bold theme-text-primary mb-2">AI Chat Assistant</h3>
        <p className="theme-text-secondary">Select a team to start chatting with your AI coaching assistant</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Chat Header */}
      <div className={`cgm-card rounded-2xl p-6`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Bot className="w-10 h-10 cgm-icon-data flex-shrink-0" />
            <div>
              <h2 className="text-2xl font-bold theme-text-primary">AI Assistant</h2>
              <p className="theme-text-secondary">
                Ask questions about {selectedTeam.name} • Last {days} days
              </p>
            </div>
          </div>
          
          <button
            onClick={clearChat}
            className={`px-4 py-2 rounded-lg ${isDark ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-100 hover:bg-gray-200'} theme-text-primary transition-all duration-200 flex items-center space-x-2`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
            <span>Clear Chat</span>
          </button>
        </div>
      </div>

      {/* Chat Messages */}
      <div className={`cgm-card rounded-2xl overflow-hidden`}>
        <div className="h-[600px] overflow-y-auto p-6 space-y-4">
          {messages.map((message) => (
            <div
              key={message.id}
              className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-3xl px-4 py-3 rounded-2xl ${
                  message.type === 'user'
                    ? isDark ? 'bg-white text-black' : 'bg-gray-900 text-white'
                    : 'cgm-list-item theme-text-primary'
                } ${message.loading ? 'animate-pulse' : ''}`}
              >
                {message.type === 'ai' && (
                  <div className="flex items-center space-x-2 mb-2">
                    <Bot className="w-5 h-5 cgm-icon-data" />
                    <span className="text-sm font-medium theme-text-secondary">
                      AI Assistant
                    </span>
                  </div>
                )}
                
                {message.type === 'ai' ? (
                  <div className="prose prose-sm max-w-none">
                    <ReactMarkdown 
                      remarkPlugins={[remarkGfm]}
                      components={{
                        // Custom styling for different markdown elements
                        h1: ({ children }) => <h1 className="text-xl font-bold mb-3 theme-text-primary">{children}</h1>,
                        h2: ({ children }) => <h2 className="text-lg font-bold mb-2 theme-text-primary">{children}</h2>,
                        h3: ({ children }) => <h3 className="text-base font-bold mb-2 theme-text-primary">{children}</h3>,
                        p: ({ children }) => <p className="mb-2 theme-text-primary leading-relaxed">{children}</p>,
                        ul: ({ children }) => <ul className="mb-3 pl-4 space-y-1">{children}</ul>,
                        ol: ({ children }) => <ol className="mb-3 pl-4 space-y-1">{children}</ol>,
                        li: ({ children }) => <li className="theme-text-primary list-disc">{children}</li>,
                        strong: ({ children }) => <strong className="font-semibold theme-text-primary">{children}</strong>,
                        em: ({ children }) => <em className="italic theme-text-secondary">{children}</em>,
                        code: ({ children }) => (
                          <code className={`px-1.5 py-0.5 rounded text-xs font-mono ${isDark ? 'bg-gray-600 text-gray-200' : 'bg-gray-200 text-gray-700'}`}>
                            {children}
                          </code>
                        ),
                        blockquote: ({ children }) => (
                          <blockquote className={`border-l-4 ${isDark ? 'border-blue-400 bg-gray-700/30' : 'border-blue-500 bg-blue-50'} pl-4 py-2 my-3 italic`}>
                            {children}
                          </blockquote>
                        ),
                      }}
                    >
                      {message.content}
                    </ReactMarkdown>
                  </div>
                ) : (
                  <div className="whitespace-pre-wrap">{message.content}</div>
                )}
                
                {/* Source Citations for AI responses */}
                {message.type === 'ai' && message.sources && message.sources.length > 0 && (
                  <div className={`mt-3 pt-3 border-t ${isDark ? 'border-gray-600' : 'border-gray-300'}`}>
                    <p className={`text-xs font-medium mb-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                      Knowledge Base Sources:
                    </p>
                    <div className="space-y-1">
                      {message.sources.map((source, idx) => (
                        <p key={idx} className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                          • {source.document_title} ({(source.similarity * 100).toFixed(0)}% relevant)
                        </p>
                      ))}
                    </div>
                  </div>
                )}
                
                <div className={`text-xs mt-2 ${
                  message.type === 'user' ? 'text-blue-100' : 'theme-text-secondary'
                }`}>
                  {message.timestamp.toLocaleTimeString()}
                </div>
              </div>
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>

        {/* Chat Input */}
        <div className={`border-t ${isDark ? 'border-gray-700/50' : 'border-gray-200/50'} p-4`}>
          <div className="flex space-x-4">
            <div className="flex-1">
              <textarea
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Ask about team performance, data patterns, insights, or get recommendations..."
                className={`w-full px-4 py-3 rounded-xl border ${isDark ? 'border-gray-600 bg-gray-700' : 'border-gray-300 bg-white'} theme-text-primary focus:ring-2 focus:ring-[var(--accent-ui-focus)] focus:border-transparent transition-all duration-200 resize-none`}
                rows={2}
                disabled={isLoading}
              />
            </div>
            <button
              onClick={sendMessage}
              disabled={!inputMessage.trim() || isLoading}
              className={`px-6 py-3 rounded-xl font-medium transition-all duration-200 flex items-center space-x-2 ${
                !inputMessage.trim() || isLoading
                  ? `${isDark ? 'bg-gray-700' : 'bg-gray-200'} ${isDark ? 'text-gray-500' : 'text-gray-400'} cursor-not-allowed`
                  : 'cgm-button transform hover:scale-105'
              }`}
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                  <span>Thinking...</span>
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                  </svg>
                  <span>Send</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Suggested Questions */}
      {messages.length <= 1 && (
        <div className={`cgm-card rounded-2xl p-6`}>
          <h3 className="text-lg font-bold theme-text-primary mb-4 flex items-center gap-2">
            <Lightbulb className="w-5 h-5" /> Suggested Questions
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {getSuggestedQuestions().map((question, index) => (
              <button
                key={index}
                onClick={() => setInputMessage(question)}
                className="cgm-list-item text-left p-3 rounded-lg theme-text-primary transition-all duration-200 hover:scale-105"
              >
                <span className="text-sm">{question}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/30 rounded-2xl p-4">
          <div className="flex items-center space-x-3">
            <XCircle className="w-8 h-8 cgm-icon-warning flex-shrink-0" />
            <div>
              <h4 className="font-medium theme-text-primary">Error</h4>
              <p className="text-sm theme-text-secondary">{error}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CoachChat; 